import { QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Navigate, Route, Routes } from "react-router-dom";
import { trpc, trpcClient, queryClient } from "./hooks/trpc";
import { LoginPage } from "./pages/LoginPage";
import { StammdatenPage } from "./pages/StammdatenPage";
import { ProfilPage } from "./pages/ProfilPage";
import { DatenschutzPage } from "./pages/DatenschutzPage";
import { EinstellungenPage } from "./pages/EinstellungenPage";
import { Layout } from "./components/Layout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { SatelliteView } from "./components/shell/SatelliteView";
import { HubAdminPanel } from "./pages/HubAdminPanel";
import { HomePage } from "./pages/HomePage";
import { GfOnlyRoute } from "./components/auth/GfOnlyRoute";
import { StoryAccessRoute } from "./components/auth/StoryAccessRoute";
import { useAllowedApps } from "./hooks/useAllowedApps";
import { useAuth } from "./hooks/useAuth";
import { getDefaultLandingRoute, SHELL_APPS } from "./lib/apps";
import { useInstanceBranding } from "./hooks/useInstanceBranding";

// Single source of truth for all SatelliteView routes. The Rail uses the same
// SHELL_APPS registry, therefore new satellites cannot accidentally navigate to
// an existing view because App.tsx was not updated in parallel.
const SATELLITE_ROUTE_APPS = SHELL_APPS.filter((app) => app.kind === "satellite");

// Hash-Router: Jede App lebt unter ihrem eigenen Hash (`#/hub`, `#/notes`, …).
// Vorteil: Iframes der Satellites laufen unter denselben absoluten Pfaden
// (`/notes/*`, …) wie bisher und treffen direkt die Reverse-Proxy-Logik
// in `server/index.ts` — der Hub-SPA wird nur fuer `/` ausgeliefert.
//
// Routing-Logik:
//   - `/` loest die Default-Landing-Page aus (= `/home`)
//   - `/home` ist der zentrale, RBAC-gefilterte App-Launcher
//   - `/hub` leitet auf `/home` weiter
//   - `/admin/:view` ist der einzige Administrationsbereich (Schildsymbol)
//   - Satellite-Apps werden nur durch eine bewusste Navigation geöffnet

function LandingPage() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const { allowed, loading } = useAllowedApps();

  if (isLoading || (isAuthenticated && loading)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--color-bg)" }}>
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full animate-pulse-dot" style={{ background: "var(--color-primary)" }} />
          <span className="text-sm" style={{ color: "var(--color-muted)" }}>Laden…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.forcePasswordChange) {
    return <Navigate to="/hub/konto" replace state={{ forcePasswordChange: true }} />;
  }

  return <Navigate to={getDefaultLandingRoute(allowed)} replace />;
}

export function App() {
  useInstanceBranding();

  return (
    <ErrorBoundary>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <HashRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/datenschutz" element={<DatenschutzPage />} />

              <Route element={<Layout />}>
                {/* Zentrale Home-/Launcher-Seite */}
                <Route path="/home" element={<HomePage />} />

                {/* Hub-eigene Views unter /hub/* */}
                <Route path="/hub" element={<Navigate to="/home" replace />} />
                {/* Stammdaten liegen in der Daten-Gruppe der Administration. Der
                    alte Pfad bleibt als Lesezeichen-kompatible Weiterleitung. */}
                <Route path="/hub/stammdaten" element={<Navigate to="/admin/stammdaten" replace />} />
                <Route path="/hub/konto" element={<ProfilPage />} />
                <Route path="/hub/einstellungen" element={<EinstellungenPage />} />
                {/* Fruehere Reiter, heute Seiten im Adminbereich */}
                <Route path="/hub/admin" element={<Navigate to="/admin" replace />} />
                <Route path="/hub/audit" element={<Navigate to="/admin/audit" replace />} />
                <Route path="/hub/import" element={<Navigate to="/admin/import" replace />} />
                <Route path="/hub/security" element={<Navigate to="/admin/security" replace />} />
                <Route path="/hub/monitoring" element={<Navigate to="/admin/monitoring" replace />} />
                <Route path="/hub/banken" element={<Navigate to="/admin/banken" replace />} />
                <Route path="/hub/berichte" element={<Navigate to="/admin/berichte" replace />} />

                {/* Die einzige Ausnahme im sonst GF-geschuetzten Adminbereich:
                    Verwaltung darf ausschliesslich die eigene Kontaktdatenpflege
                    unter Stammdaten erreichen. */}
                <Route path="/admin/stammdaten" element={<GfOnlyRoute scope="contactData"><HubAdminPanel view="stammdaten" /></GfOnlyRoute>} />
                <Route path="/admin" element={<GfOnlyRoute><HubAdminPanel /></GfOnlyRoute>} />
                {/* Nutzer und Personen ist seit 04.11.2026 Teil der Benutzerseite. */}
                <Route path="/admin/personen" element={<Navigate to="/admin/users" replace />} />
                <Route path="/admin/:view" element={<GfOnlyRoute><HubAdminPanel /></GfOnlyRoute>} />

                {/* Satellite-Slots — Iframe via SatelliteView.
                    `key` ist Pflicht: Ohne key recycelt React die SatelliteView-
                    Instanz beim Wechsel zwischen Satellite-Routen (gleicher
                    Komponententyp an gleicher Stelle im Tree), das iframe-`src`
                    bleibt am alten Wert kleben und die neue App laedt nicht.
                    Die Einträge stammen direkt aus SHELL_APPS, also derselben
                    Registry wie die Rail-Navigation. */}
                {SATELLITE_ROUTE_APPS.map((app) => (
                  <Route
                    key={`sat-route-${app.id}`}
                    path={app.route}
                    element={app.id === "story"
                      ? <StoryAccessRoute><SatelliteView key={`sat-${app.id}`} appId={app.id} /></StoryAccessRoute>
                      : <SatelliteView key={`sat-${app.id}`} appId={app.id} />}
                  />
                ))}
              </Route>

              {/* Legacy-Pfade (Bookmarks etc.) auf neue Routen umlenken */}
              <Route path="/apps" element={<Navigate to="/home" replace />} />
              <Route path="/stammdaten" element={<Navigate to="/admin/stammdaten" replace />} />
              <Route path="/audit" element={<Navigate to="/admin/audit" replace />} />
              <Route path="/import" element={<Navigate to="/admin/import" replace />} />
              <Route path="/konto" element={<Navigate to="/hub/konto" replace />} />
              <Route path="/security" element={<Navigate to="/admin/security" replace />} />
              <Route path="/einstellungen" element={<Navigate to="/hub/einstellungen" replace />} />
              <Route path="/fair-kapa" element={<Navigate to="/fair" replace />} />
              <Route path="/back-office" element={<Navigate to="/backoffice" replace />} />
              <Route path="/notes" element={<Navigate to="/nexus" replace />} />

              {/* Root und Catch-All → zentrale Hub-Home-Seite */}
              <Route path="/" element={<LandingPage />} />
              <Route path="*" element={<LandingPage />} />
            </Routes>
          </HashRouter>
        </QueryClientProvider>
      </trpc.Provider>
    </ErrorBoundary>
  );
}
